import React from "react";
import { LinkInfo } from "../models/shared/LinkInfo";
import { isNullOrWhiteSpace } from "../shared/string";
import Button, { ButtonSize } from "./Buttons/Button";
import PortableText, { PortableTextBody } from "./PortableText";
import { ElementClasses, getPortableTextColorTheme, PortableTextColorTheme } from "./PortableText/PortableText";
import { checkLinkExists, renderLinkTitle } from "./SanityLink";

export interface FactListProps {
    title?: string;
    titleClasses?: string;
    portableTextContent?: PortableTextBody;
    _rawPortableTextContent?: PortableTextBody;
    primaryButton?: LinkInfo;
    secondaryButton?: LinkInfo;
    portableTextColorTheme?: PortableTextColorTheme;
    portableTextCustomElementClasses?: ElementClasses;
    facts?: {
        value: string;
        unit: string;
        description: string;
    }[];
}

const FactList = (props: FactListProps) => {
    const {
        title,
        titleClasses = "text-current",
        portableTextContent,
        _rawPortableTextContent,
        primaryButton,
        secondaryButton,
        portableTextColorTheme = PortableTextColorTheme.None,
        portableTextCustomElementClasses,
        facts
    } = props;

    let {
        elementClasses,
        primaryButtonColor = "primary"
    } = getPortableTextColorTheme(portableTextColorTheme, portableTextCustomElementClasses);

    if (elementClasses?.p) {
        elementClasses.p += " text-lg lg:text-2xl";
    }
    else {
        elementClasses = {
            p: "text-current text-lg lg:text-2xl"
        };
    }

    return (
        <aside className="flex flex-wrap justify-center xl:justify-between">
            <div className="min-w-sm-padding w-full xl:w-7/12 flex flex-col xl:pr-8 mb-12 xl:mb-8">
                {!isNullOrWhiteSpace(title) && (
                    <h2 className={titleClasses}>
                        {title}
                    </h2>
                )}
                {(_rawPortableTextContent || portableTextContent) && 
                    <PortableText 
                        _rawBody={_rawPortableTextContent || portableTextContent} 
                        addSections={false} 
                        colorTheme={portableTextColorTheme}
                        elementClasses={elementClasses}
                    />}
                <div className="flex flex-col sm:items-center pt-4 lg:pt-12 xl:flex-row">
                    {checkLinkExists(primaryButton) && 
                        <Button
                            className="xl:mr-6"
                            size={ButtonSize.Normal}
                            color={primaryButtonColor}
                            type="link"
                            {...primaryButton}>
                            {renderLinkTitle(primaryButton!)}
                        </Button>}
                    {checkLinkExists(secondaryButton) && 
                        <Button
                            className="mt-6 xl:mt-0"
                            size={ButtonSize.Normal}
                            color={"primary-inverted"}
                            type="link"
                            {...secondaryButton}>
                            {renderLinkTitle(secondaryButton!)}
                        </Button>}
                </div>
            </div>
            {!!facts?.length && 
                <div className="xl:w-5/12 xl:pl-12">
                    <dl className="flex flex-col">
                        {facts?.map((fact, index: number) => (
                            <div
                                key={index}
                                className="flex items-center py-2"
                            >
                                <dt className="flex-shrink-0 w-24 h-24 flex flex-col justify-center items-center p-4 mr-6 rounded-full bg-facts-list-circle-bg lg:w-32 lg:h-32">
                                    <div className="text-2xl lg:text-4xl text-brand-primary-20 font-bold leading-tight text-center">
                                        {fact.value}
                                    </div>
                                    <div className="text-sm lg:text-base uppercase text-subheader-primary font-bold text-center">
                                        {fact.unit}
                                    </div>
                                </dt>
                                <dd className="text-current text-base lg:text-xl">
                                    {fact.description}
                                </dd>
                            </div>
                        ))}
                    </dl>
                </div>}
        </aside>
    );
};

export default FactList;